// Workouts and sets. Both get their ids on the phone (UUIDv7) and are uploaded as whole records,
// newest edit winning, so logging works with no signal and a retried upload never duplicates anything
// (docs/SCOPE.md, Offline). Times are epoch milliseconds from the phone's clock.
import { z } from 'zod';
import { Units } from './auth.ts';
import { Bests, PrKind } from './records.ts';
import { convertWeight, distanceUnit, metresPer, roundWeight } from './units.ts';
import { IsoDate } from './schedule.ts';
import type { Exercise, ExerciseType, MuscleGroup } from './training.ts';

const Id = z.string().uuid();
const Time = z.number().int().min(0);

export const WorkoutRecord = z.object({
  id: Id,
  /** The calendar day it counts for, in the person's time zone. */
  date: IsoDate,
  splitId: z.string().max(100).nullable(),
  name: z.string().trim().min(1).max(60),
  /** Exercises in the order they were added, including ones with no sets yet. */
  exerciseIds: z.array(z.string().max(100)).max(60),
  notes: z.string().max(4000).nullable(),
  startedAt: Time,
  endedAt: Time.nullable(),
  updatedAt: Time,
  deletedAt: Time.nullable(),
  /** Workouts done together share this (the planner logs everyone's sets from one screen). Older records lack it. */
  sessionId: z.string().max(100).nullable().default(null),
});
export type WorkoutRecord = z.infer<typeof WorkoutRecord>;

export const SetRecord = z.object({
  id: Id,
  workoutId: Id,
  exerciseId: z.string().max(100),
  /** In `unit`, as entered. */
  weight: z.number().min(0).max(5000).nullable(),
  unit: Units,
  reps: z.number().int().min(0).max(1000).nullable(),
  durationS: z.number().int().min(0).max(86_400).nullable(),
  distanceM: z.number().min(0).max(1_000_000).nullable(),
  rpe: z.number().min(1).max(10).multipleOf(0.5).nullable(),
  warmup: z.boolean(),
  loggedAt: Time,
  updatedAt: Time,
  deletedAt: Time.nullable(),
});
export type SetRecord = z.infer<typeof SetRecord>;

/** Whose record it is; left out, the uploader's own. Only the planner can upload someone else's. */
const Owner = { userId: z.string().max(100).optional() };
export const SyncRequest = z.object({
  workouts: z.array(WorkoutRecord.extend(Owner)).max(200),
  sets: z.array(SetRecord.extend(Owner)).max(2000),
});
export type SyncRequest = z.infer<typeof SyncRequest>;

/** Records the server can never accept (e.g. an exercise that no longer exists), so the phone stops retrying them. */
export const SyncResponse = z.object({
  ok: z.literal(true),
  rejected: z.array(z.object({ id: z.string(), reason: z.string() })),
});
export type SyncResponse = z.infer<typeof SyncResponse>;

export const WorkoutDetail = z.object({
  userId: z.string(),
  userName: z.string(),
  workout: WorkoutRecord,
  sets: z.array(SetRecord),
  /** PRs set in this workout, by set id, judged against everything logged before it. */
  prs: z.record(z.string(), z.array(PrKind)),
});
export type WorkoutDetail = z.infer<typeof WorkoutDetail>;

export const WorkoutSummary = WorkoutRecord.extend({ userId: z.string(), setCount: z.number().int(), exerciseCount: z.number().int() });
export type WorkoutSummary = z.infer<typeof WorkoutSummary>;
export const WorkoutList = z.object({ workouts: z.array(WorkoutSummary) });

/** Someone's record with one exercise: how often and how recently, and their last session's sets. */
export const ExerciseHistory = z.object({
  exerciseId: z.string(),
  lastDate: IsoDate,
  /** Workouts with this exercise in the last 90 days. */
  recentCount: z.number().int(),
  lastSets: z.array(SetRecord),
  /** The session before that (targets need two to spot two misses in a row). Older saved packs lack it. */
  prevSets: z.array(SetRecord).default([]),
  /** All-time records, so a PR can be flagged on the phone even with no signal. */
  bests: Bests,
});
export type ExerciseHistory = z.infer<typeof ExerciseHistory>;

/** Someone's sessions with one exercise, newest first. */
export const ExerciseSessions = z.object({
  userId: z.string(),
  sessions: z.array(z.object({ workoutId: z.string(), date: IsoDate, name: z.string(), sets: z.array(SetRecord) })),
});
export type ExerciseSessions = z.infer<typeof ExerciseSessions>;

/** What the phone saves for logging without signal: recent numbers, and workouts still in progress. */
export const LogPack = z.object({ history: z.array(ExerciseHistory), open: z.array(WorkoutDetail) });
export type LogPack = z.infer<typeof LogPack>;

// --- What a set holds, by exercise type ---

export type SetValues = Pick<SetRecord, 'weight' | 'reps' | 'durationS' | 'distanceM'>;

/** The fields an exercise type records; everything else stays null. */
export const FIELDS: Record<ExerciseType, (keyof SetValues)[]> = {
  weight_reps: ['weight', 'reps'],
  bodyweight_reps: ['reps'],
  hold: ['durationS'],
  cardio: ['durationS', 'distanceM'],
};

/** Starting values for a type that has never been logged. */
const FRESH: Record<ExerciseType, SetValues> = {
  weight_reps: { weight: null, reps: 8, durationS: null, distanceM: null },
  bodyweight_reps: { weight: null, reps: 10, durationS: null, distanceM: null },
  hold: { weight: null, reps: null, durationS: 30, distanceM: null },
  cardio: { weight: null, reps: null, durationS: 20 * 60, distanceM: null },
};

/**
 * Pre-fill for the next set of an exercise: the previous set in this workout if there is one, otherwise the
 * matching set from last time (set 2 pre-fills from last time's set 2), otherwise a sensible start.
 * Weights come back in the person's units.
 */
export function prefill(type: ExerciseType, thisWorkout: SetRecord[], lastTime: SetRecord[], units: Units): SetValues {
  const working = (l: SetRecord[]) => l.filter((s) => !s.deletedAt);
  const now = working(thisWorkout);
  const before = working(lastTime);
  // Warm-ups pre-fill warm-ups' successors poorly; prefer working sets from last time.
  const lastWorking = before.filter((s) => !s.warmup);
  const source = now.at(-1) ?? (lastWorking[now.length] ?? lastWorking.at(-1) ?? before.at(-1));
  const out: SetValues = { ...FRESH[type] };
  if (!source) return out;
  for (const f of FIELDS[type]) {
    const value = source[f];
    if (value === null) continue;
    out[f] = f === 'weight' ? roundWeight(convertWeight(value, source.unit, units), units) : value;
  }
  return out;
}

/** "135 lb × 8", "12 reps", "0:45", "20:00 · 3.1 mi". */
export function describeSet(type: ExerciseType, s: SetValues & { unit: Units }, units: Units): string {
  const parts: string[] = [];
  if (type === 'weight_reps') {
    const w = s.weight === null ? null : roundWeight(convertWeight(s.weight, s.unit, units), units);
    parts.push(w === null ? `${s.reps ?? 0} reps` : `${w} ${units} × ${s.reps ?? 0}`);
  } else if (type === 'bodyweight_reps') {
    parts.push(`${s.reps ?? 0} reps`);
  } else {
    if (s.durationS !== null) parts.push(formatDuration(s.durationS));
    if (type === 'cardio' && s.distanceM !== null) parts.push(`${+(s.distanceM / metresPer(units)).toFixed(2)} ${distanceUnit(units)}`);
  }
  return parts.join(' · ');
}

/** 45 → "0:45", 3725 → "1:02:05". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// --- Suggestions ---

export type Suggestion = { exercise: Exercise; history: ExerciseHistory | undefined; score: number };

// "Tricep" and "triceps", "pull-up" and "pullup", "dumbbell" and "db" spellings all count as the same word.
const words = (s: string) =>
  s
    .toLowerCase()
    .replace(/[-']/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));

/**
 * Exercises whose names match a search, best first. Words can come in any order and can be the start of a word
 * ("tri ext"); with three or more words, one may miss ("seated tricep curl machine" still finds the machine).
 */
export function searchExercises<T extends { name: string }>(exercises: T[], query: string): T[] {
  const want = words(query);
  if (!want.length) return exercises;
  const scored = exercises.map((e) => {
    const have = words(e.name);
    return { e, hits: want.filter((w) => have.some((h) => h.startsWith(w))).length, extra: have.length };
  });
  const needed = want.length > 2 ? want.length - 1 : want.length;
  // Most words matched first, then the closest names (fewest other words).
  return scored
    .filter((x) => x.hits >= needed)
    .sort((a, b) => b.hits - a.hits || a.extra - b.extra)
    .map((x) => x.e);
}

/**
 * Exercises for a workout's muscle groups, the ones you do most often and most recently first.
 * Main-muscle matches outrank exercises that only work the groups as a helper.
 */
export function suggestExercises(
  exercises: Exercise[],
  groups: readonly MuscleGroup[],
  history: ExerciseHistory[],
  today: string,
  exclude: readonly string[] = [],
): Suggestion[] {
  const byId = new Map(history.map((h) => [h.exerciseId, h]));
  const wanted = new Set(groups);
  const out: Suggestion[] = [];
  for (const exercise of exercises) {
    if (exclude.includes(exercise.id)) continue;
    const primary = exercise.primaryMuscles.filter((m) => wanted.has(m)).length;
    const secondary = exercise.secondaryMuscles.filter((m) => wanted.has(m)).length;
    if (!primary && !secondary) continue;
    const h = byId.get(exercise.id);
    let score = primary ? 10 + primary : 2 + secondary * 0.5;
    if (h) {
      const daysAgo = Math.max(0, (Date.parse(today) - Date.parse(h.lastDate)) / 86_400_000);
      // Habit (how often) plus recency (fades over about a month).
      score += 20 + Math.min(h.recentCount, 12) * 3 + 15 * Math.exp(-daysAgo / 30);
    }
    out.push({ exercise, history: h, score });
  }
  return out.sort((a, b) => b.score - a.score || a.exercise.name.localeCompare(b.exercise.name));
}

// --- Bodyweight (private: only ever your own) ---

export const BodyweightEntry = z.object({ date: IsoDate, weight: z.number().min(20).max(1500), unit: Units });
export type BodyweightEntry = z.infer<typeof BodyweightEntry>;
export const BodyweightInput = BodyweightEntry.omit({ date: true });
export const BodyweightList = z.object({ entries: z.array(BodyweightEntry) });
